import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Barcode, ArrowLeft, MessageCircle, Mail, Clock, HelpCircle, LifeBuoy, BookOpen } from "lucide-react";

const WHATSAPP_URL = "https://w.app/carlos_soglia";
const SUPPORT_EMAIL = "suporte@magicsearchimagesproducts.com";

export default function Support() {
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-bold text-lg text-[#09090B]">
            <div className="w-8 h-8 rounded-md brand-primary flex items-center justify-center">
              <Barcode className="w-4 h-4 text-white" />
            </div>
            <span>MAGIC SIP</span>
          </Link>
          <Link to="/login">
            <Button variant="outline" className="rounded-md border-zinc-300 h-9" data-testid="support-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Voltar
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-8 py-10">
        <div className="eyebrow mb-2 flex items-center gap-2">
          <LifeBuoy className="w-4 h-4 text-[#002FA7]" /> Central de Atendimento
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#09090B] mb-2">
          Precisa de ajuda?
        </h1>
        <p className="text-sm text-zinc-600 mb-8 max-w-2xl">
          Escolha o canal mais rápido para tirar sua dúvida — de configuração de ERP a rollback de
          publicação em massa. Respondemos todos os canais em até 24h úteis.
        </p>

        {/* Contact channels */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          {/* WhatsApp — primary */}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            className="group border-2 border-emerald-200 bg-emerald-50 rounded-md p-6 hover:border-emerald-500 hover:shadow-md transition-all"
            data-testid="support-whatsapp"
          >
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-md bg-emerald-600 text-white flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-lg text-[#09090B] mb-1">
                  WhatsApp
                </div>
                <p className="text-sm text-zinc-700 mb-2">
                  Fale direto com Carlos Soglia — canal mais rápido para dúvidas urgentes.
                </p>
                <div className="text-xs font-mono text-emerald-800 truncate">
                  w.app/carlos_soglia
                </div>
              </div>
            </div>
          </a>

          {/* Email */}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="group border-2 border-zinc-200 bg-white rounded-md p-6 hover:border-[#002FA7] hover:shadow-md transition-all"
            data-testid="support-email"
          >
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-md bg-[#002FA7] text-white flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Mail className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-lg text-[#09090B] mb-1">
                  E-mail
                </div>
                <p className="text-sm text-zinc-700 mb-2">
                  Para relatórios detalhados, prints ou anexos técnicos.
                </p>
                <div className="text-xs font-mono text-zinc-700 truncate">
                  {SUPPORT_EMAIL}
                </div>
              </div>
            </div>
          </a>
        </div>

        {/* Availability */}
        <div className="border border-zinc-200 bg-white rounded-md p-5 mb-8 flex items-start gap-3">
          <Clock className="w-5 h-5 text-[#002FA7] flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-[#09090B] mb-1 text-sm">
              Horário de atendimento
            </div>
            <p className="text-xs text-zinc-600">
              Segunda a sexta · 9h às 18h (horário de Brasília) · Fora desse horário, mensagens no
              WhatsApp são respondidas no próximo dia útil.
            </p>
          </div>
        </div>

        {/* Quick help */}
        <section className="mb-8">
          <div className="eyebrow mb-3 flex items-center gap-2">
            <HelpCircle className="w-4 h-4" /> Perguntas frequentes
          </div>
          <div className="space-y-3">
            <FAQ
              q="Como conectar minha loja Nuvemshop?"
              a="Vá em Configurações → Nuvemshop → 'Conectar Nuvemshop'. Você será redirecionado para autorizar o app via OAuth (1 clique)."
            />
            <FAQ
              q="Como cancelar minha assinatura?"
              a="Configurações → Assinatura → botão 'Cancelar'. Sem multa. Ao cancelar, os produtos publicados via Magic SIP têm imagens/descrições substituídas por placeholder — restaurados automaticamente se você reativar."
            />
            <FAQ
              q="Como faço rollback de uma publicação errada?"
              a="Na tabela do batch, após o publish, aparece o botão 'Apagar publicados'. Digite APAGAR para confirmar e todos os produtos publicados naquele lote são removidos da Nuvemshop."
            />
            <FAQ
              q="A IA não gerou boa descrição para meu produto."
              a="Edite manualmente na tabela do batch antes de publicar. Ou envie na planilha uma coluna `descricao` — quando presente, a IA é ignorada e o valor da planilha vai direto para o Nuvemshop."
            />
            <FAQ
              q="Preço da planilha não subiu no Nuvemshop?"
              a="Verifique se a coluna se chama `preco` (aceita também `price`, `valor`, `preço`). Formatos aceitos: 12.90, 12,90, 1.234,90, R$ 24,50. Se ainda não subir, envie um print no WhatsApp."
            />
          </div>
        </section>

        {/* Manual link */}
        <div className="border border-zinc-200 bg-white rounded-md p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-md brand-primary text-white flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[#09090B] text-sm mb-0.5">
              Manual de uso completo
            </div>
            <p className="text-xs text-zinc-600">
              Guia passo a passo com 9 seções — disponível para usuários com plano ativo ou trial.
            </p>
          </div>
          <Link to="/manual">
            <Button variant="outline" className="rounded-md border-zinc-300" data-testid="support-manual-link">
              Ver manual
            </Button>
          </Link>
        </div>
      </main>

      <footer className="border-t border-zinc-200 bg-white py-6 mt-10">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-xs text-zinc-500 font-mono flex justify-between flex-wrap gap-3">
          <span>MAGIC SIP · v1.0 · UTILITÁRIO PARA E-COMMERCE</span>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-[#002FA7]">Política de Privacidade</Link>
            <Link to="/support" className="hover:text-[#002FA7]">Suporte</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FAQ({ q, a }) {
  return (
    <details className="border border-zinc-200 bg-white rounded-md p-4 group">
      <summary className="cursor-pointer font-semibold text-sm text-[#09090B] flex items-center justify-between gap-2 list-none">
        <span>{q}</span>
        <span className="text-zinc-400 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
      </summary>
      <p className="text-xs text-zinc-600 mt-2 leading-relaxed">{a}</p>
    </details>
  );
}
